import { useMutation, useQuery } from "@apollo/react-hooks";
import { Checkbox, ChoiceList, Modal, Spinner, Stack } from "@shopify/polaris";
import React, { useCallback, useEffect } from "react";
import { ADMIN_RECREATE_ORDER_FULFILLMENT_V2 } from "../../../graphql/mutations";
import { handleError, reducerFn } from "../../../helper";
import { useToastContext } from "../../shared/ToastContext";
import { GET_ORDER_RELATIONS } from "../../../graphql/queries";
import { get } from "lodash";

export const ReSentOrderPolaris = ({
  open,
  toggleShowModal,
  value,
  refetch,
  changeStatusCount,
  convertOS,
}) => {
  const orderId = value?.id;
  const { setNotify, toggleToast } = useToastContext();

  const [state, setState] = React.useReducer(reducerFn, {
    relatedOrderIds: value?.id ? [value.id] : [],
    options: [],
    showRelatedOrder: false,
    markPause: false,
  });

  const { data, loading: l2 } = useQuery(GET_ORDER_RELATIONS, {
    variables: {
      originID: value?.originId,
    },
    skip: !open || !value?.originId,
  });

  React.useEffect(() => {
    const ids = getOrders(data);
    setState({ options: ids });
  }, [data]);

  const timer = React.useRef(null);
  const [adminRecreateOrderFulfillment, { loading }] = useMutation(
    ADMIN_RECREATE_ORDER_FULFILLMENT_V2,
    {
      onError: (err) => {
        setNotify({ msg: handleError(err?.toString()), err: true });
      },
      onCompleted: () => {
        setNotify({ msg: "Re-submit order success.", err: false });
        timer.current && clearTimeout(timer.current);
        timer.current = setTimeout(() => {
          toggleShowModal && toggleShowModal(false);
          changeStatusCount &&
            changeStatusCount(convertOS && [convertOS.Fulfilled]);
          refetch && refetch();
        }, 2100);
      },
    },
  );

  const handleChange = React.useCallback((value, id) => {
    setState({ [id]: value });
  }, []);

  const handleSubmit = useCallback(() => {
    if (orderId) {
      toggleToast && toggleToast(true);
      setNotify && setNotify({ msg: null, err: false });
      adminRecreateOrderFulfillment({
        variables: {
          orderId,
          markPause: state.markPause,
          relatedOrderIds:
            !!state.showRelatedOrder && state.relatedOrderIds.length > 0
              ? state.relatedOrderIds
              : undefined,
        },
      });
    }
  }, [
    orderId,
    state.relatedOrderIds,
    state.showRelatedOrder,
    state.markPause,
    toggleToast,
    setNotify,
    adminRecreateOrderFulfillment,
  ]);

  useEffect(() => {
    return () => timer.current && clearTimeout(timer.current);
  }, []);

  return (
    <>
      <Modal
        open={open}
        onClose={toggleShowModal}
        sectioned
        title={`Resent order: #${orderId ? orderId : ""}`}
        secondaryActions={[{ content: "No", onAction: toggleShowModal }]}
        primaryAction={{
          content: "Yes",
          onAction: handleSubmit,
          loading: loading,
        }}
      >
        <Stack vertical>
          <p id="resent_modal">
            Do you want to re-submit this order to Fulfillment?
          </p>

          <Stack vertical spacing="extraTight">
            <Checkbox
              label="Mark pause"
              checked={state.markPause}
              id="markPause"
              onChange={handleChange}
            />

            <Checkbox
              label="Select related order"
              checked={state.showRelatedOrder}
              id="showRelatedOrder"
              onChange={handleChange}
            />

            <div style={{ marginLeft: "1rem" }}>
              {l2 ? (
                <Spinner />
              ) : state.showRelatedOrder ? (
                <ChoiceList
                  selected={state.relatedOrderIds}
                  choices={state.options}
                  name="relatedOrderIds"
                  onChange={handleChange}
                  allowMultiple
                />
              ) : null}
            </div>
          </Stack>
        </Stack>
      </Modal>
    </>
  );
};

function getOrders(data) {
  const nodes = get(data, "getOrderRelations") || [];

  return Array.from(new Set(nodes.map((i) => i?.id).filter(Boolean))).map(
    (item) => ({ value: item + "", label: item + "" }),
  );
}
